import { useState, type CSSProperties, type ReactNode } from 'react';
import { Badge, Button, Icon, IconButton, PulseDot, SidePanel, useToast } from '../ui';
import type { BadgeTone } from '../ui';
import { useUser } from '../../context/UserContext';
import { addOpportunityToQueue, contentTypeForOpportunity, CONTENT_TYPES, TIERS } from '../../lib/keywordSweep';
import {
  moveOpportunities,
  perMonth,
  seoAction,
  seoKind,
  seoStageOf,
  seoStatus,
  updateOpportunity,
  type SeoBaseline,
  type SeoOpportunity,
  type SeoStatus,
} from '../../lib/seoOpportunities';
import StatusMenu, { StatusTrigger } from './StatusMenu';

/**
 * One opportunity, in full.
 *
 * The order is the argument: what fixing it is worth, what Google is seeing, why it was flagged,
 * then what the agent proposes and the evidence it read to get there. The evidence is not an
 * appendix — it is the part that lets a person say "that's wrong" without opening the page
 * themselves, which is the only reason to trust the rest.
 */

interface Props {
  opportunity: SeoOpportunity | null;
  onClose: () => void;
  onChanged: () => void | Promise<void>;
  onDiagnose: (id: string) => void;
}

const num = (n: number | null | undefined, digits = 0) =>
  n == null ? '—' : n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });

const path = (url: string) => url.replace(/^https?:\/\/(www\.)?/, '');

// "2026-08-19..2026-09-15" → "Aug 19 → Sep 15", same as the stat card on the list.
const formatWindow = (window: string | null | undefined) => {
  if (!window) return null;
  const fmt = (iso: string) => {
    const d = new Date(`${iso}T12:00:00`);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };
  return window.split('..').map(fmt).join(' → ');
};

const TONE_COLOR: Record<BadgeTone, string> = {
  up: 'var(--status-up)',
  warn: 'var(--status-warn)',
  down: 'var(--status-down)',
  info: 'var(--status-info)',
  idle: 'var(--status-idle)',
  accent: 'var(--accent-text)',
  neutral: 'var(--text-muted)',
};

const CONFIDENCE_TONE: Record<string, BadgeTone> = { high: 'up', medium: 'warn', low: 'down' };

// Where Google usually cuts a SERP listing. A guide for the reader, not a rule the agent broke.
const TITLE_LIMIT = 60;
const META_LIMIT = 160;

const text = {
  body: { fontSize: 'var(--text-sm)', color: 'var(--text-body)', lineHeight: 1.6, margin: 0 } as CSSProperties,
  muted: { fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 1.55, margin: 0 } as CSSProperties,
  faint: { fontSize: 'var(--text-xs)', color: 'var(--text-faint)', lineHeight: 1.45, margin: 0 } as CSSProperties,
};

const box: CSSProperties = { border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-md)', background: 'var(--surface-canvas)' };

function Section({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '10px' }}>
      <header style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px' }}>
        <h3 className="t-eyebrow" style={{ margin: 0 }}>
          {title}
        </h3>
        {aside ? <span style={{ ...text.faint, whiteSpace: 'nowrap' }}>{aside}</span> : null}
      </header>
      {children}
    </section>
  );
}

/** Cells in one bordered block; the 1px gap over a hairline background draws the dividers at any wrap. */
function StatGrid({ min = 118, children }: { min?: number; children: ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
        gap: '1px',
        background: 'var(--border-hairline)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  );
}

function Stat({ label, value, hint, color }: { label: string; value: ReactNode; hint?: ReactNode; color?: string }) {
  return (
    <div style={{ background: 'var(--surface-canvas)', padding: '12px 14px', minWidth: 0 }}>
      <div className="t-eyebrow" style={{ fontSize: 'var(--text-2xs)', marginBottom: '8px' }}>
        {label}
      </div>
      <div className="t-num" style={{ fontSize: 'var(--text-lg)', lineHeight: 1.1, color: color ?? 'var(--text-body)' }}>
        {value}
      </div>
      {hint ? <div style={{ ...text.faint, marginTop: '5px' }}>{hint}</div> : null}
    </div>
  );
}

/** A page on the client's site, as a row: what it is called, where it lives, and a number or two. */
function PageRow({
  label,
  title,
  url,
  meta,
  highlight,
}: {
  label?: ReactNode;
  title?: string | null;
  url: string;
  meta?: ReactNode;
  highlight?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '11px 14px',
        background: highlight ? 'var(--surface-raised)' : 'var(--surface-canvas)',
      }}
    >
      {label ? <div style={{ flexShrink: 0, minWidth: '64px', paddingTop: '1px' }}>{label}</div> : null}
      <div style={{ minWidth: 0, flex: 1 }}>
        {title !== undefined ? (
          <div style={{ fontSize: 'var(--text-sm)', color: title ? 'var(--text-body)' : 'var(--text-faint)', lineHeight: 1.4, marginBottom: '3px' }}>
            {title || 'No title recorded'}
          </div>
        ) : null}
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="t-num"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            // With no title line above it, the URL is the row's headline.
            fontSize: title === undefined ? 'var(--text-sm)' : 'var(--text-xs)',
            color: title === undefined ? 'var(--text-body)' : 'var(--text-faint)',
            paddingTop: title === undefined ? '2px' : 0,
            minWidth: 0,
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{path(url)}</span>
          <Icon name="external-link" size={11} style={{ flexShrink: 0 }} />
        </a>
      </div>
      {meta ? (
        <div className="t-num" style={{ flexShrink: 0, textAlign: 'right', fontSize: 'var(--text-xs)', color: 'var(--text-muted)', lineHeight: 1.5 }}>
          {meta}
        </div>
      ) : null}
    </div>
  );
}

// Every single-column grid here is minmax(0, 1fr), not the implicit auto track: a nowrap URL's
// min-content would otherwise widen the track past the panel and clip the numbers on the right.
function RowList({ children }: { children: ReactNode }) {
  return (
    <div
      style={{ ...box, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '1px', background: 'var(--border-hairline)', overflow: 'hidden' }}
    >
      {children}
    </div>
  );
}

/** A fact with a yes/no reading. `ok` is whether it is good for the page, not whether it is true. */
function Fact({ ok, children, detail }: { ok: boolean; children: ReactNode; detail?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', background: 'var(--surface-canvas)', minWidth: 0 }}>
      <span
        aria-label={ok ? 'Pass' : 'Fail'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '18px',
          height: '18px',
          borderRadius: 'var(--radius-pill)',
          flexShrink: 0,
          color: ok ? 'var(--status-up)' : 'var(--status-warn)',
          background: ok ? 'var(--status-up-soft)' : 'var(--status-warn-soft)',
        }}
      >
        <Icon name={ok ? 'check' : 'x'} size={11} strokeWidth={2.5} />
      </span>
      <span style={{ fontSize: 'var(--text-sm)', color: ok ? 'var(--text-muted)' : 'var(--text-body)', flex: 1, minWidth: 0 }}>{children}</span>
      {detail != null ? (
        <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', flexShrink: 0 }}>
          {detail}
        </span>
      ) : null}
    </div>
  );
}

function Callout({ tone, icon, children }: { tone: 'warn' | 'info'; icon: ReactNode; children: ReactNode }) {
  const color = tone === 'warn' ? 'var(--status-warn)' : 'var(--status-info)';
  return (
    <div
      style={{
        display: 'flex',
        gap: '10px',
        alignItems: 'flex-start',
        padding: '11px 14px',
        borderRadius: 'var(--radius-md)',
        background: tone === 'warn' ? 'var(--status-warn-soft)' : 'var(--status-info-soft)',
        color,
      }}
    >
      <span style={{ display: 'inline-flex', flexShrink: 0, paddingTop: '2px' }}>{icon}</span>
      <div style={{ ...text.muted, color: 'var(--text-body)' }}>{children}</div>
    </div>
  );
}

/** Copy the agent wants shipped, with its length against where Google usually truncates. */
function ShipCopy({ label, value, limit, onCopy }: { label: string; value: string; limit: number; onCopy: () => void }) {
  const over = value.length > limit;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '6px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
        <span className="t-eyebrow" style={{ fontSize: 'var(--text-2xs)' }}>
          {label}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <span
            className="t-num"
            title={`Google usually cuts this off past about ${limit} characters`}
            style={{ fontSize: 'var(--text-xs)', color: over ? 'var(--status-warn)' : 'var(--text-faint)' }}
          >
            {value.length}/{limit}
          </span>
          <IconButton icon="copy" size="sm" label={`Copy ${label.toLowerCase()}`} onClick={onCopy} />
        </span>
      </div>
      <div style={{ ...box, padding: '10px 12px', fontSize: 'var(--text-sm)', lineHeight: 1.5, color: 'var(--text-body)' }}>{value}</div>
    </div>
  );
}

/** Before → after for one metric. `lowerIsBetter` for position. */
function Delta({
  label,
  before,
  after,
  digits = 0,
  suffix = '',
  lowerIsBetter = false,
}: {
  label: string;
  before: number | null | undefined;
  after: number | null | undefined;
  digits?: number;
  suffix?: string;
  lowerIsBetter?: boolean;
}) {
  const diff = before != null && after != null ? after - before : null;
  const better = diff != null && diff !== 0 && (lowerIsBetter ? diff < 0 : diff > 0);
  const color = diff == null || diff === 0 ? 'var(--text-faint)' : better ? 'var(--status-up)' : 'var(--status-down)';
  return (
    <Stat
      label={label}
      value={`${num(after, digits)}${after == null ? '' : suffix}`}
      hint={
        <span className="t-num">
          from {num(before, digits)}
          {before == null ? '' : suffix}
          {diff != null && diff !== 0 ? (
            <span style={{ color, marginLeft: '6px' }}>
              {diff > 0 ? '+' : '−'}
              {num(Math.abs(diff), digits)}
            </span>
          ) : null}
        </span>
      }
    />
  );
}

type Competing = { url: string; title: string | null; position: number; impressions: number; clicks?: number };

/** What the detector itself stored, per kind — shown before any agent has read the page. */
function KindEvidence({ o, ev }: { o: SeoOpportunity; ev: Record<string, unknown> }) {
  const str = (k: string) => (typeof ev[k] === 'string' ? (ev[k] as string) : null);
  const n = (k: string) => (typeof ev[k] === 'number' ? (ev[k] as number) : null);

  if (Array.isArray(ev.competing) && ev.competing.length) {
    const rows = [...(ev.competing as Competing[])].sort((a, b) => a.position - b.position);
    return (
      <>
        <RowList>
          {rows.map((c) => (
            <PageRow
              key={c.url}
              highlight={c.url === o.url}
              label={
                <span className="t-num" style={{ fontSize: 'var(--text-md)', color: 'var(--text-body)' }}>
                  #{num(c.position, 1)}
                </span>
              }
              title={c.title}
              url={c.url}
              meta={
                <>
                  {num(c.impressions)} impr
                  <br />
                  {num(c.clicks ?? null)} clicks
                </>
              }
            />
          ))}
        </RowList>
        {n('total_impressions') != null ? (
          <p className="t-num" style={text.faint}>
            {num(n('total_impressions'))} impressions split across {rows.length} pages
            {n('expected_ctr_at_best') != null
              ? ` · one page at #${num(rows[0].position, 1)} should earn ~${num(n('expected_ctr_at_best'), 1)}% CTR`
              : ''}
          </p>
        ) : null}
      </>
    );
  }

  const betterUrl = str('better_url');
  if (betterUrl) {
    const rankingUrl = str('ranking_url') ?? o.url;
    return (
      <RowList>
        {rankingUrl ? (
          <PageRow
            label={<Badge tone="neutral">Ranks</Badge>}
            title={str('ranking_title')}
            url={rankingUrl}
            meta={`#${num(o.baseline?.position, 1)}`}
          />
        ) : null}
        <PageRow
          highlight
          label={<Badge tone="info">Titled</Badge>}
          title={str('better_title')}
          url={betterUrl}
          meta={n('better_inlinks') != null ? `${num(n('better_inlinks'))} links in` : undefined}
        />
      </RowList>
    );
  }

  const currentlyRanking = str('currently_ranking');
  if (currentlyRanking) {
    return (
      <RowList>
        <PageRow label={<Badge tone="neutral">Shown</Badge>} url={currentlyRanking} meta={`#${num(o.baseline?.position, 1)}`} />
      </RowList>
    );
  }

  const title = str('title');
  if (!title && !o.url) return null;
  const facts: ReactNode[] = [];
  if (typeof ev.query_in_title === 'boolean')
    facts.push(
      <Fact key="t" ok={ev.query_in_title}>
        Phrase in the title
      </Fact>
    );
  if (n('expected_ctr') != null)
    facts.push(
      <Fact
        key="c"
        ok={(n('measured_ctr') ?? 0) >= (n('expected_ctr') ?? 0)}
        detail={`${num(n('measured_ctr'), 1)}% of ${num(n('expected_ctr'), 1)}%`}
      >
        CTR against its position
      </Fact>
    );
  if (n('inlinks') != null)
    facts.push(
      <Fact key="l" ok={(n('inlinks') ?? 0) > 0} detail={num(n('inlinks'))}>
        Internal links in
      </Fact>
    );
  if (typeof ev.is_indexable === 'boolean')
    facts.push(
      <Fact key="i" ok={ev.is_indexable}>
        Indexable
      </Fact>
    );
  if (n('word_count') != null)
    facts.push(
      <Fact key="w" ok detail={num(n('word_count'))}>
        Words on the page
      </Fact>
    );
  return (
    <>
      {o.url ? (
        <RowList>
          <PageRow label={<Badge tone="neutral">Ranks</Badge>} title={title} url={o.url} />
        </RowList>
      ) : null}
      {facts.length ? (
        <div
          style={{
            ...box,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
            gap: '1px',
            background: 'var(--border-hairline)',
            overflow: 'hidden',
          }}
        >
          {facts}
        </div>
      ) : null}
    </>
  );
}

export default function OpportunityPanel({ opportunity, onClose, onChanged, onDiagnose }: Props) {
  const toast = useToast();
  const { user } = useUser();
  const [saving, setSaving] = useState(false);
  if (!opportunity) return <SidePanel open={false} onClose={onClose} />;

  const o = opportunity;
  const kind = seoKind(o.kind);
  const action = seoAction(o.action_type);
  const ev = (o.evidence ?? {}) as Record<string, unknown>;
  const measured = (ev.measured ?? null) as Record<string, unknown> | null;
  const proposedTitle = ev.proposed_title as string | null | undefined;
  const proposedMeta = ev.proposed_meta as string | null | undefined;
  const risk = ev.risk as string | null | undefined;
  const confidence = ev.confidence as string | null | undefined;
  const targetPosition = typeof ev.target_position === 'number' ? ev.target_position : null;
  const tier = o.tier ? TIERS[o.tier] : null;
  // The detector writes "A: <reason>"; the badge already says A.
  const tierReason = (o.tier_reason || tier?.blurb || '').replace(/^[AB]:\s*/, '');
  const gain = perMonth(o.est_click_gain);
  const window = formatWindow(o.baseline?.window);
  // New pages and A-tier expansions are content work: they go to the client's content queue.
  const queueType = contentTypeForOpportunity(o);

  const set = async (patch: Partial<SeoOpportunity>, message: string) => {
    setSaving(true);
    try {
      await updateOpportunity(o.id, patch);
      toast.success(message);
      await onChanged();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  // A hand move keeps the panel open: the row is still this row, it just lives on another tab now.
  const move = async (to: SeoStatus) => {
    setSaving(true);
    try {
      await moveOpportunities([o.id], to);
      toast.success(`Moved to ${seoStageOf(to).label}`, seoStatus(to).label);
      await onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not move it');
    } finally {
      setSaving(false);
    }
  };

  const addToQueue = async () => {
    setSaving(true);
    try {
      await addOpportunityToQueue(o, user?.id ?? null);
      toast.success(
        'Added to the content queue',
        `${queueType ? CONTENT_TYPES[queueType].label : 'Entry'}${o.client?.name ? ` · ${o.client.name}` : ''}`
      );
      await onChanged();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not add it to the content queue');
    } finally {
      setSaving(false);
    }
  };

  const copy = async (value: string, what: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${what} copied`);
    } catch {
      toast.error('Could not copy');
    }
  };

  const b: Partial<SeoBaseline> = o.baseline ?? {};

  return (
    <SidePanel
      open
      onClose={onClose}
      width={620}
      eyebrow={
        <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
          <Badge tone={kind.tone}>{kind.label}</Badge>
          <StatusMenu current={o.status} jobOut={o.status === 'diagnosing'} disabled={saving} onMove={move}>
            <StatusTrigger status={o.status} disabled={saving} />
          </StatusMenu>
          {o.client?.name ? <span style={{ color: 'var(--text-faint)', marginLeft: '2px' }}>{o.client.name}</span> : null}
        </span>
      }
      title={<span style={{ display: 'block', fontSize: 'var(--text-lg)', lineHeight: 1.3, marginTop: '4px' }}>{o.query}</span>}
      footer={
        <span style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
          {o.content_queue_id ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: 'var(--text-xs)',
                color: 'var(--text-faint)',
                marginRight: 'auto',
              }}
            >
              <Icon name="check" size={13} /> In the content queue
            </span>
          ) : queueType ? (
            <Button variant="secondary" size="sm" icon="plus" onClick={addToQueue} disabled={saving} style={{ marginRight: 'auto' }}>
              Add to Content Queue
            </Button>
          ) : null}
          {/* Same word as the button on the list, because it is the same action. While a job is
              out there is nothing to press. */}
          {o.status === 'open' || o.status === 'diagnosed' ? (
            <Button variant="secondary" size="sm" icon="sparkles" onClick={() => onDiagnose(o.id)} disabled={saving}>
              {o.diagnosed_at ? 'Diagnose again' : 'Diagnose'}
            </Button>
          ) : null}
          {o.status !== 'dismissed' ? (
            <Button variant="secondary" size="sm" onClick={() => set({ status: 'dismissed' }, 'Dismissed')} disabled={saving}>
              Dismiss
            </Button>
          ) : (
            <Button variant="secondary" size="sm" icon="rotate-ccw" onClick={() => set({ status: 'open' }, 'Reopened')} disabled={saving}>
              Reopen
            </Button>
          )}
          {o.status === 'diagnosed' ? (
            <Button size="sm" icon="check" onClick={() => set({ status: 'queued' }, 'Queued for the build')} disabled={saving}>
              Accept
            </Button>
          ) : null}
          {o.status === 'queued' ? (
            <Button
              size="sm"
              icon="rocket"
              onClick={() => set({ status: 'shipped', shipped_at: new Date().toISOString() }, 'Marked shipped — it settles in 28 days')}
              disabled={saving}
            >
              Mark shipped
            </Button>
          ) : null}
        </span>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '28px' }}>
        {/* The headline: what it is worth, and whether it is the kind of page that can turn into a customer. */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: tier ? 'repeat(auto-fit, minmax(220px, 1fr))' : '1fr',
            gap: '1px',
            background: 'var(--border-hairline)',
            border: '1px solid var(--border-hairline)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
          }}
        >
          <div style={{ background: 'var(--surface-canvas)', padding: '16px 18px' }}>
            <div className="t-eyebrow" style={{ fontSize: 'var(--text-2xs)', marginBottom: '10px' }}>
              Worth if fixed
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
              <span className="t-num" style={{ fontSize: 'var(--text-2xl)', lineHeight: 1, color: gain ? 'var(--status-up)' : 'var(--text-faint)' }}>
                {gain == null ? '—' : `+${num(gain)}`}
              </span>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>clicks / month</span>
            </div>
            <p style={{ ...text.faint, marginTop: '8px' }}>
              {targetPosition != null ? `If it reaches position ${targetPosition}` : 'At the CTR its position should earn'}
            </p>
          </div>
          {tier ? (
            <div style={{ background: 'var(--surface-canvas)', padding: '16px 18px' }}>
              <div className="t-eyebrow" style={{ fontSize: 'var(--text-2xs)', marginBottom: '10px' }}>
                Tier
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span
                  className="t-num"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '30px',
                    height: '30px',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--text-md)',
                    color: TONE_COLOR[tier.tone],
                    background: tier.tone === 'accent' ? 'var(--accent-soft)' : 'var(--surface-raised)',
                  }}
                >
                  {o.tier}
                </span>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
                  {o.tier === 'A' ? 'Can turn into a customer' : 'Builds authority'}
                </span>
              </div>
              <p style={{ ...text.faint, marginTop: '8px' }}>{tierReason}</p>
            </div>
          ) : null}
        </div>

        <Section title="Search Console" aside={window}>
          <StatGrid>
            <Stat
              label="Position"
              value={num(b.position, 1)}
              hint={targetPosition != null ? <span className="t-num">target {targetPosition}</span> : undefined}
            />
            <Stat label="Impressions" value={num(b.impressions)} />
            <Stat label="Clicks" value={num(b.clicks)} />
            <Stat
              label="CTR"
              value={`${num(b.ctr, 1)}%`}
              hint={typeof ev.expected_ctr === 'number' ? <span className="t-num">expected {num(ev.expected_ctr, 1)}%</span> : undefined}
            />
          </StatGrid>
        </Section>

        <Section title="Why it's flagged">
          <p style={{ ...text.muted, paddingLeft: '12px', borderLeft: `2px solid ${TONE_COLOR[kind.tone]}` }}>{kind.blurb}</p>
          <KindEvidence o={o} ev={ev} />
        </Section>

        {o.status === 'diagnosing' ? (
          <Callout tone="info" icon={<PulseDot tone="info" />}>
            An agent is reading the page and the repo behind it. The diagnosis arrives as the job's spec, on the Spec queue in{' '}
            <a href="/fulfillment" style={{ color: 'var(--accent-text)' }}>
              Fulfillment
            </a>
            .
          </Callout>
        ) : null}

        {o.recommendation ? (
          <Section
            title="Proposed"
            aside={
              <span className="t-num">
                {o.model ?? 'agent'}
                {o.diagnosed_at ? ` · ${new Date(o.diagnosed_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}` : ''}
              </span>
            }
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr)',
                gap: '16px',
                padding: '16px 18px',
                border: '1px solid var(--border-strong)',
                borderRadius: 'var(--radius-lg)',
                background: 'var(--surface-raised)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '4px' }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: 'var(--text-md)',
                      color: 'var(--text-body)',
                      fontWeight: 'var(--weight-semibold)' as CSSProperties['fontWeight'],
                    }}
                  >
                    <Icon name="sparkles" size={15} style={{ color: 'var(--accent-text)' }} />
                    {action ? action.label : 'The agent says'}
                  </span>
                  {action ? <span style={text.faint}>{action.blurb}</span> : null}
                </div>
                {confidence ? <Badge tone={CONFIDENCE_TONE[confidence] ?? 'neutral'}>{confidence} confidence</Badge> : null}
              </div>

              <p style={text.body}>{o.recommendation}</p>

              {proposedTitle ? (
                <ShipCopy label="Title to ship" value={proposedTitle} limit={TITLE_LIMIT} onCopy={() => copy(proposedTitle, 'Title')} />
              ) : null}
              {proposedMeta ? (
                <ShipCopy label="Description to ship" value={proposedMeta} limit={META_LIMIT} onCopy={() => copy(proposedMeta, 'Description')} />
              ) : null}

              {risk ? (
                <Callout tone="warn" icon={<Icon name="triangle-alert" size={14} />}>
                  {risk}
                </Callout>
              ) : null}
            </div>
          </Section>
        ) : null}

        {measured ? (
          <Section title="What it measured on the page">
            <div
              style={{
                ...box,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: '1px',
                background: 'var(--border-hairline)',
                overflow: 'hidden',
              }}
            >
              <Fact ok={Boolean(measured.query_in_title)}>Phrase in the title tag</Fact>
              <Fact ok={Boolean(measured.query_in_h1)}>Phrase in the H1</Fact>
              <Fact ok={Boolean(measured.query_in_first_100_words)}>Phrase in the opening</Fact>
              <Fact
                ok={!measured.meta_description_missing}
                detail={measured.meta_description_length ? `${String(measured.meta_description_length)} ch` : undefined}
              >
                Meta description
              </Fact>
              <Fact ok={!measured.title_likely_truncated} detail={`${String(measured.title_length)} ch`}>
                Title fits the SERP
              </Fact>
              <Fact ok={measured.h1_count === 1} detail={String(measured.h1_count)}>
                Exactly one H1
              </Fact>
              <Fact ok={Boolean(measured.indexable)}>Indexable</Fact>
              <Fact
                ok={Number(measured.anchors_matching_query) > 0}
                detail={`${String(measured.anchors_matching_query)}/${String(measured.internal_link_count)}`}
              >
                Anchors using the phrase
              </Fact>
            </div>
            <StatGrid min={110}>
              <Stat label="Words" value={num(Number(measured.word_count))} />
              <Stat
                label="Phrase used"
                value={`${String(measured.phrase_hits)}×`}
                hint={<span className="t-num">{String(measured.phrase_density_pct)}% density</span>}
              />
              <div style={{ background: 'var(--surface-canvas)', padding: '12px 14px', minWidth: 0 }}>
                <div className="t-eyebrow" style={{ fontSize: 'var(--text-2xs)', marginBottom: '8px' }}>
                  Schema
                </div>
                {Array.isArray(measured.schema_types) && measured.schema_types.length ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {(measured.schema_types as string[]).map((t) => (
                      <Badge key={t} tone="neutral" style={{ textTransform: 'none', letterSpacing: 0 }}>
                        {t}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--status-warn)' }}>None</span>
                )}
              </div>
            </StatGrid>
          </Section>
        ) : null}

        {o.result_28d ? (
          <Section
            title="How it actually went"
            aside={
              o.shipped_at
                ? `28 days after ${new Date(o.shipped_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
                : '28 days after shipping'
            }
          >
            <StatGrid>
              <Delta label="Position" before={b.position} after={o.result_28d.position} digits={1} lowerIsBetter />
              <Delta label="Clicks" before={b.clicks} after={o.result_28d.clicks} />
              <Delta label="CTR" before={b.ctr} after={o.result_28d.ctr} digits={1} suffix="%" />
            </StatGrid>
          </Section>
        ) : null}
      </div>
    </SidePanel>
  );
}
